import { useState } from 'react';
import { Stack, Box, Divider } from '@mantine/core';
import { IconLock, IconLogout } from '@tabler/icons-react';
import { Avatar, Text, Button, PasswordInput } from '../../components/ui';
import { useEnrichedAuth } from '../../hooks/useEnrichedAuth';

interface ScreenLockModalProps {
  opened: boolean;
  onUnlock: () => void;
}

/**
 * ScreenLockModal Component
 * Full-screen modal that locks the UI
 * Requires password verification via SDK authentication to unlock
 */
export default function ScreenLockModal({ opened, onUnlock }: ScreenLockModalProps) {
  const { user, login, logout } = useEnrichedAuth();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleUnlock = async () => {
    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }

    if (!user?.email) {
      setError('User email not found');
      return;
    }

    setIsUnlocking(true);
    setError('');

    try {
      // Verify password using SDK authentication
      await login(user.email, password, false);

      // If login succeeds, password is correct - unlock the screen
      onUnlock();
      setPassword('');
      setError('');
    } catch (err: any) {
      setError('Invalid password. Please try again.');
    } finally {
      setIsUnlocking(false);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      onUnlock(); // Close the lock screen
      window.location.href = '/auth/login';
    } catch (err) {
      console.error('Logout failed:', err);
      // Still redirect to login even if logout fails
      onUnlock();
      window.location.href = '/auth/login';
    } finally {
      setIsLoggingOut(false);
    }
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    const name = user.displayName || user.username || 'User';
    return name
      .split(' ')
      .map((n: string) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  if (!opened) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        style={{
          backgroundColor: 'var(--mantine-color-body)',
          padding: '3rem',
          borderRadius: '12px',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        }}
      >
        <Stack align="center" gap="lg">
          {/* Lock Icon */}
          <Box
            style={{
              padding: 20,
              borderRadius: '50%',
              backgroundColor: 'var(--mantine-color-red-1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconLock size={40} color="var(--mantine-color-red-6)" />
          </Box>

          {/* User Avatar */}
          <Avatar size="xl" radius="xl" color="indigo" src={user?.avatar || undefined}>
            {getUserInitials()}
          </Avatar>

          {/* User Info */}
          <Stack gap={4} align="center">
            <Text size="xl" fw={700}>
              {user?.displayName || user?.username || 'User'}
            </Text>
            <Text size="md" c="dimmed">
              Screen Locked
            </Text>
          </Stack>

          {/* Password Input */}
          <Stack gap="md" w="100%" mt="md">
            <PasswordInput
              placeholder="Enter password to unlock"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleUnlock();
                }
              }}
              error={error}
              autoFocus
              size="lg"
            />

            {/* Unlock Button */}
            <Button
              fullWidth
              onClick={handleUnlock}
              loading={isUnlocking}
              disabled={!password.trim()}
              size="lg"
              variant="gradient"
              gradientVariant="primary"
            >
              Unlock
            </Button>

            <Divider label="or" labelPosition="center" my="xs" />

            {/* Logout Button */}
            <Button
              fullWidth
              onClick={handleLogout}
              loading={isLoggingOut}
              disabled={isUnlocking}
              size="md"
              variant="subtle"
              color="gray"
              leftSection={<IconLogout size={18} />}
            >
              Sign out and log in as different user
            </Button>
          </Stack>

          {/* Help Text */}
          <Text size="sm" c="dimmed" ta="center" mt="sm">
            Enter your password to unlock and resume your session
          </Text>
        </Stack>
      </Box>
    </div>
  );
}
